// Start a call — the same right-side Drawer the schedule flow uses, holding the
// directory instead of a form.
//
// Pick one person and it is an ordinary direct call; pick several and everyone
// rings at once (Layer B5). The list is searchable and filterable by
// availability, because the question you actually have when starting a call is
// "who is around right now".
//
// Live status comes from presenceStore, and each row subscribes to its OWN
// status (§9.3.1) so someone coming online repaints one row, not the drawer.

import { ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';

import { api, DirectoryEntry } from '../api';
import { MAX_CALL_PEERS } from '../lib/calls';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { Drawer } from '../ui/Drawer';
import { IconCheck, IconSearch } from '../ui/icons';
import { PresenceStatus, statusOf, usePresenceStore } from '../store/presenceStore';

type Filter = 'all' | 'online' | 'offline';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'online', label: 'Online' },
  { id: 'offline', label: 'Offline' },
];

/** "In a call" counts as around — you can still ring them. */
function isOnline(status: PresenceStatus): boolean {
  return status !== 'offline';
}

export interface CallTarget {
  id: string;
  name: string;
}

export function NewCallDrawer({
  onClose,
  onStart,
  title = 'New call',
  cta,
  excludeIds,
  onCopyLink,
}: {
  onClose: () => void;
  /** Ring the picked people. The drawer stays up (busy) until this settles. */
  onStart: (targets: CallTarget[]) => Promise<void>;
  /** "New call", or "Add to call" when used from inside one. */
  title?: string;
  /** Overrides the action label; defaults to the start-a-call wording. */
  cta?: string;
  /** People already on the call — they cannot be picked again. */
  excludeIds?: string[];
  /** Offered alongside the list: the link works for anyone, directory or not. */
  onCopyLink?: () => void;
}) {
  const [rows, setRows] = useState<DirectoryEntry[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [picked, setPicked] = useState<CallTarget[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .directory()
      .then(setRows)
      .catch(() => setFailed(true));
  }, []);

  const onQ = useCallback((e: ChangeEvent<HTMLInputElement>) => setQ(e.target.value), []);

  const toggle = useCallback((person: CallTarget) => {
    setPicked((cur) =>
      cur.some((p) => p.id === person.id)
        ? cur.filter((p) => p.id !== person.id)
        : cur.length >= MAX_CALL_PEERS
          ? cur
          : [...cur, person],
    );
  }, []);

  const start = useCallback(() => {
    if (picked.length === 0) return;
    setBusy(true);
    void onStart(picked).finally(() => setBusy(false));
  }, [picked, onStart]);

  const pickedIds = useMemo(() => new Set(picked.map((p) => p.id)), [picked]);
  const full = picked.length >= MAX_CALL_PEERS;

  const exclude = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);

  return (
    <Drawer title={title} onClose={onClose}>
      <div className="bz-ncall-toolbar">
        <div className="bz-people-search">
          <IconSearch size={16} />
          <input
            type="search"
            placeholder="Search people"
            value={q}
            onChange={onQ}
            aria-label="Search people"
          />
        </div>
        <div className="bz-ncall-filters" role="group" aria-label="Filter by availability">
          {FILTERS.map((f) => (
            <FilterChip key={f.id} id={f.id} label={f.label} on={filter === f.id} onPick={setFilter} />
          ))}
        </div>
      </div>

      {failed && <p className="bz-people-note bz-error-text">Could not load the directory.</p>}
      {!failed && rows === null && <p className="bz-people-note">Loading…</p>}

      {rows !== null && (
        <PickList
          rows={rows}
          q={q}
          filter={filter}
          picked={pickedIds}
          exclude={exclude}
          full={full}
          onToggle={toggle}
        />
      )}

      <div className="bz-ncall-foot">
        <p className="bz-ncall-count">
          {picked.length === 0
            ? 'Pick who to call'
            : picked.length === 1
              ? `Calling ${picked[0]!.name}`
              : `${picked.length} people — everyone rings at once`}
          {full && <span className="bz-ncall-max"> · max {MAX_CALL_PEERS}</span>}
        </p>
        <div className="bz-modal-actions">
          {onCopyLink ? (
            <Button onClick={onCopyLink}>Copy link</Button>
          ) : (
            <Button onClick={onClose}>Cancel</Button>
          )}
          <Button variant="primary" onClick={start} disabled={busy || picked.length === 0}>
            {busy ? 'Calling…' : (cta ?? (picked.length > 1 ? 'Start group call' : 'Start call'))}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

const FilterChip = memo(function FilterChip({
  id,
  label,
  on,
  onPick,
}: {
  id: Filter;
  label: string;
  on: boolean;
  onPick: (f: Filter) => void;
}) {
  const pick = useCallback(() => onPick(id), [id, onPick]);
  return (
    <button
      type="button"
      className={on ? 'bz-ncall-filter bz-ncall-filter-on' : 'bz-ncall-filter'}
      onClick={pick}
      aria-pressed={on}
    >
      {label}
    </button>
  );
});

/** The filtered roster. Availability is read here so the filter reacts to live
 * presence, while each row still owns its own dot. */
function PickList({
  rows,
  q,
  filter,
  picked,
  exclude,
  full,
  onToggle,
}: {
  rows: DirectoryEntry[];
  q: string;
  filter: Filter;
  picked: Set<string>;
  exclude: Set<string>;
  full: boolean;
  onToggle: (p: CallTarget) => void;
}) {
  // Only what the FILTER needs: the rows keep owning their own dot.
  const connected = usePresenceStore((s) => s.connected);
  const statuses = usePresenceStore((s) => s.statuses);
  const list = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (r.self || exclude.has(r.id)) return false; // already on the call
      if (ql && !r.name.toLowerCase().includes(ql) && !(r.email ?? '').toLowerCase().includes(ql)) {
        return false;
      }
      if (filter === 'all') return true;
      const status = connected ? (statuses[r.id] ?? 'offline') : r.status;
      return filter === 'online' ? isOnline(status) : !isOnline(status);
    });
  }, [rows, q, filter, connected, statuses, exclude]);

  if (list.length === 0) {
    return (
      <p className="bz-people-note">
        {q ? 'No one matches your search.' : `No one is ${filter === 'offline' ? 'offline' : 'online'} right now.`}
      </p>
    );
  }

  return (
    <div className="bz-ncall-rows">
      {list.map((p) => (
        <PickRow
          key={p.id}
          person={p}
          picked={picked.has(p.id)}
          disabled={full && !picked.has(p.id)}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}

/** One selectable person. Memoized + subscribed to its OWN status (§9.3). */
const PickRow = memo(function PickRow({
  person,
  picked,
  disabled,
  onToggle,
}: {
  person: DirectoryEntry;
  picked: boolean;
  disabled: boolean;
  onToggle: (p: CallTarget) => void;
}) {
  const status = usePresenceStore((s) => (s.connected ? statusOf(s, person.id) : person.status));
  const toggle = useCallback(
    () => onToggle({ id: person.id, name: person.name }),
    [onToggle, person.id, person.name],
  );

  return (
    <button
      type="button"
      className={picked ? 'bz-ncall-row bz-ncall-row-on' : 'bz-ncall-row'}
      onClick={toggle}
      disabled={disabled}
      aria-pressed={picked}
    >
      <span className="bz-person-avatar" data-status={status}>
        <Avatar name={person.name} identity={person.id} size={36} photoUrl={person.avatar} />
        <span className={`bz-person-dot bz-person-dot-${status}`} aria-hidden="true" />
      </span>
      <span className="bz-ncall-body">
        <span className="bz-person-name">{person.name}</span>
        {person.email && <span className="bz-person-email">{person.email}</span>}
      </span>
      <span className={picked ? 'bz-ncall-check bz-ncall-check-on' : 'bz-ncall-check'}>
        {picked && <IconCheck size={13} />}
      </span>
    </button>
  );
});
